import { isAbortError } from '@adv-frontend/error';
import type { Flow } from '../../contract/flow';
import { ComputedFlowBase } from '../base/instance';
import { FlowComputation, type FlowComputationContext } from './computation';

/**
 * Функция, которая вычисляет значение для ComputedFlow.
 *
 * Эта функция получает контекст вычисления, который может быть использован для отслеживания
 * зависимостей и вызывается всякий раз, когда вычисленное значение нужно пересчитать.
 *
 * @typeParam T - Тип значения, которое возвращает эта функция-геттер
 * @param ctx - Контекст вычисления, используемый для отслеживания зависимостей
 * @returns Вычисленное значение типа T
 */
export type ComputedFlowGetter<T> = (ctx: FlowComputationContext) => T;

/**
 * Опции конфигурации для создания ComputedFlow.
 *
 * @typeParam T - Тип вычисленного значения
 */
export interface ComputedFlowOptions<T> {
    /**
     * Начальное значение для использования, когда вычисление завершается с ошибкой прерывания
     * и нет доступного кешированного значения.
     */
    initialValue?: T;

    /**
     * Функция для сравнения вычисленных значений, чтобы определить, изменились ли они.
     *
     * Это используется для предотвращения ненужных обновлений, когда вычисленное значение
     * эквивалентно предыдущему значению, даже если они не строго равны.
     *
     * @param a - Предыдущее вычисленное значение
     * @param b - Новое вычисленное значение
     * @returns `true`, если значения считаются равными, `false` в противном случае
     * @default Object.is
     *
     * @example
     * ```typescript
     * // Пользовательское сравнение объектов
     * equals: (a, b) => deepEqual(a, b)
     * ```
     */
    equals?: (a: T, b: T) => boolean;
}

/**
 * Синхронный вычисляемый поток, который автоматически пересчитывает свое значение
 * при изменении его зависимостей.
 *
 * @typeParam T - Тип значения, которое вычисляет и выдает этот поток
 *
 * @example
 * ```typescript
 * const counter = createFlow(0);
 * const doubled = new ComputedFlow(ctx => {
 *   return ctx.watch(counter) * 2;
 * });
 *
 * console.log(doubled.getSnapshot()); // 0
 * counter.emit(5);
 * console.log(doubled.getSnapshot()); // 10
 * ```
 */
export class ComputedFlow<T> extends ComputedFlowBase<T, FlowComputation<T>> implements Flow<T> {
    /**
     * Функция, которая вычисляет значение этого потока.
     */
    private getter: ComputedFlowGetter<T>;

    /**
     * Опции конфигурации для этого экземпляра потока
     */
    private options: ComputedFlowOptions<T> | undefined;

    /**
     * Создает новый экземпляр ComputedFlow.
     *
     * @param getter - Функция, которая вычисляет значение потока
     * @param options - Опциональная конфигурация для этого вычисляемого потока
     */
    public constructor(getter: ComputedFlowGetter<T>, options?: ComputedFlowOptions<T>) {
        super();
        this.getter = getter;
        this.options = options;
    }

    /**
     * Выполняет фактическое вычисление значения этого потока.
     *
     * @returns FlowComputation, содержащий вычисленное значение или состояние ошибки
     */
    protected compute(): FlowComputation<T> {
        const computation = new FlowComputation<T>();
        try {
            let value = this.getter(computation.getContext());

            if (this.options?.equals) {
                // Прошлое значение читаем не через getValue(): он бросает ошибку прошлого
                // вычисления, и она улетела бы в catch ниже вместо свежего успешного значения.
                // Сравнивать с ошибкой нечего — новое значение принимаем как есть.
                const cached = this.cachedComputation?.peekValue();
                if (cached && this.options.equals(value, cached.current)) {
                    value = cached.current;
                }
            }

            computation.setValue(value);
        } catch (err) {
            if (isAbortError(err)) {
                // Если вычисление было прервано, сначала попробуем использовать кешированное значение
                if (this.cachedComputation) {
                    return this.cachedComputation;
                }

                // Если кеш недоступен, используем начальное значение, если оно предоставлено
                if (hasInitialValue(this.options)) {
                    computation.setValue(this.options.initialValue);
                } else {
                    computation.setError(err);
                }
            } else {
                // Для не-прерывающих ошибок всегда устанавливаем состояние ошибки
                computation.setError(err);
            }
        } finally {
            computation.finalize();
        }
        this.onComputationFinished(computation);
        return computation;
    }
}

/**
 * Тайпгард, который проверяет, содержит ли объект опций свойство initialValue.
 *
 * @typeParam T - Тип значения
 * @param options - Объект опций для проверки
 * @returns `true`, если опции существуют и имеют свойство initialValue, `false` в противном случае
 */
function hasInitialValue<T>(options: ComputedFlowOptions<T> | undefined): options is { initialValue: T } {
    if (!options) return false;
    return Object.hasOwn(options, 'initialValue');
}
